import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Order } from 'src/app/_models/order';
import { OrderService } from 'src/app/_services/order.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-order',
    templateUrl: './order.component.html',
    styleUrls: ['./order.component.css'],
    standalone: false
})
export class OrderComponent implements OnInit {

    order: Order;
    orderNumber: string;
    loading = true;
    lineOffers: any[] = [];
    sending = false;
    senderModalRef: NgbModalRef;
    seller: any;
    lang = 'fr';

    expandedLineOfferId: number | null = null;
    loadingLineOffer = false;
    lineOfferDetail: any = null;
    totalOrder = 0;

    constructor(private orderService: OrderService,
        private router: Router,
        private modalService: NgbModal,
        private activatedRoute: ActivatedRoute,
        private toast: ToastrService,
        private translateService: TranslateService) {
        this.orderNumber = this.activatedRoute.snapshot.paramMap.get('ref_num') ?? '';
        this.lang = this.translateService.currentLang;

        if (this.orderNumber == null || this.orderNumber === '') {
            this.toast.error('Order number cannot be null');
            this.router.navigateByUrl('/admin/orders');
        }
    }

    ngOnInit(): void {
        this.orderService.loadSingleOrderByAdmin(this.orderNumber).subscribe(
            (order) => {
                this.order = order;
                this.lineOffers = order.lineOffers;
                this.lineOffers.forEach((lo: any) => this.totalOrder += lo.qty * lo.price);
            },
            (error) => this.toast.error(error),
            () => this.loading = false
        );
    }

    getDiscountedPrice(lineOffer: any): number {
        if (lineOffer.discount && lineOffer.discount > 0) {
            return lineOffer.price - (lineOffer.price * lineOffer.discount / 100);
        }
        return lineOffer.price;
    }

    hasDiscount(lineOffer: any): boolean {
        return lineOffer.discount > 0;
    }

    productPictureUrl(lineOffer: any): string {
        const picture = lineOffer.product?.picture || lineOffer.product?.defaultPicture;
        if (!picture) return '';
        return environment.image_cdn_url + '/' + picture;
    }

    getProductName(lineOffer: any): string {
        if (!lineOffer.product) return '';
        return this.lang === 'fr' && lineOffer.product.productNameFr
            ? lineOffer.product.productNameFr
            : lineOffer.product.productName;
    }

    getCategoryName(lineOffer: any): string {
        if (!lineOffer.product?.category) return '';
        return this.lang === 'fr' && lineOffer.product.category.nameFr
            ? lineOffer.product.category.nameFr
            : lineOffer.product.category.name;
    }

    getConditionDescription(lineOffer: any): string {
        if (!lineOffer.product?.productCondition) return '';
        return this.lang === 'fr' && lineOffer.product.productCondition.descriptionFr
            ? lineOffer.product.productCondition.descriptionFr
            : lineOffer.product.productCondition.description;
    }

    getMeasurementDescription(lineOffer: any): string {
        if (!lineOffer.product?.measurementUnit) return '';
        return this.lang === 'fr' && lineOffer.product.measurementUnit.descriptionFr
            ? lineOffer.product.measurementUnit.descriptionFr
            : lineOffer.product.measurementUnit.description;
    }

    getSellerName(lineOffer: any): string {
        if (!lineOffer.seller) return '';
        return lineOffer.seller.firstName + ' ' + lineOffer.seller.lastName;
    }

    deliveryAddressString(lineOffer: any): string {
        const addr = lineOffer.logisticInfo?.deliveryAddress;
        if (!addr) return '';
        return [addr.addressLine1, addr.addressLine2, addr.town, addr.country]
            .filter((v: string) => v)
            .join(', ');
    }

    getDeliveryStatus(lineOffer: any): string {
        const status = lineOffer.logisticInfo?.deliveryStatus || '';
        return status ? 'delivery.status_' + status.toLowerCase() : '';
    }

    getDeliveryStatusRaw(lineOffer: any): string {
        return lineOffer.logisticInfo?.deliveryStatus || '';
    }

    getPaymentMethodLabel(method: string): string {
        switch (method) {
            case 'FLUTTERWAVE': return 'Flutterwave';
            case 'CompanyMobileMoney': return 'Mobile Money';
            case 'COMPANY_MOBILE_MONEY': return 'Mobile Money';
            case 'CashOnDelivery': return 'Cash On Delivery';
            default: return method || '';
        }
    }

    formatDate(dateStr: string): string {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const options: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };
        return date.toLocaleDateString(this.lang === 'fr' ? 'fr-FR' : 'en-US', options);
    }

    formatCurrency(amount: number, currency?: string): string {
        if (amount == null) return '';
        return new Intl.NumberFormat(this.lang === 'fr' ? 'fr-FR' : 'en-US').format(amount) + ' ' + (currency || 'XAF');
    }

    getStatusClass(status: string): string {
        switch (status?.toUpperCase()) {
            case 'PENDING': return 'badge bg-warning text-dark';
            case 'CONFIRMED': return 'badge bg-primary';
            case 'SUCCESS': return 'badge bg-success';
            case 'SHIPPED': return 'badge bg-purple';
            case 'DELIVERED': return 'badge bg-success';
            case 'CANCELLED': return 'badge bg-danger';
            default: return 'badge bg-secondary';
        }
    }

    toggleLineOffer(lineOffer: any): void {
        if (this.expandedLineOfferId === lineOffer.id) {
            this.expandedLineOfferId = null;
            this.lineOfferDetail = null;
            return;
        }
        this.expandedLineOfferId = lineOffer.id;
        this.loadingLineOffer = true;
        this.lineOfferDetail = null;
        this.orderService.loadLineOfferByAdmin(lineOffer.id).subscribe({
            next: (res: any) => {
                this.lineOfferDetail = res;
                this.loadingLineOffer = false;
            },
            error: () => { this.loadingLineOffer = false; }
        });
    }

    getDeliverySteps(): { key: string; labelKey: string }[] {
        return [
            { key: 'PENDING',       labelKey: 'tracking.step_processing' },
            { key: 'IN_CHARGE',     labelKey: 'tracking.step_handed_over' },
            { key: 'IN_TRANSIT',    labelKey: 'tracking.step_in_transit' },
            { key: 'IN_DELIVERING', labelKey: 'tracking.step_in_delivering' },
            { key: 'DELIVERED',     labelKey: 'tracking.step_delivered' }
        ];
    }

    getCurrentDeliveryStepIndex(lineOffer: any): number {
        const status = (lineOffer.logisticInfo?.deliveryStatus || '').toUpperCase();
        const steps = this.getDeliverySteps().map(s => s.key);
        const idx = steps.indexOf(status);
        return idx >= 0 ? idx : 0;
    }

    sendConfirmationMail() {
        this.sending = true;
        this.orderService.sendConfirmationMail(this.orderNumber).subscribe(
            (response: any) => {
                this.toast.success(response.message);
            },
            (error) => {
                this.toast.error(error);
            },
            () => this.sending = false
        );
    }

    viewSellerDetails(offerDto: any, content) {
        this.seller = offerDto.seller;
        this.senderModalRef = this.modalService.open(content, { scrollable: true });
    }

    closeModal() {
        this.senderModalRef.close();
    }
}
